import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";

type OnboardingItem = {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  actionLabel?: string;
  actionHref?: string;
};

type OnboardingState = {
  completedCount: number;
  totalCount: number;
  items: OnboardingItem[];
};

const fallbackState: OnboardingState = {
  completedCount: 1,
  totalCount: 3,
  items: [
    {
      id: "app-installed",
      title: "App is installed",
      description: "The app is connected to this Shopify admin.",
      completed: true,
    },
    {
      id: "enable-app-embed",
      title: "Enable the app in your theme",
      description: "Turn on the app embed in the theme editor so the modal can appear on the storefront.",
      completed: false,
      actionLabel: "Enable app embed",
      actionHref:
        "https://admin.shopify.com/store/test-packeta-ztinfdzq/themes/163555246315/editor?context=apps&appEmbed=bd8b74ba463199acba2dc6242872ec0b%2Fapp-embed",
    },
    {
      id: "create-campaign",
      title: "Create your first campaign",
      description: "Choose the service variant, recommendations, and cart rules for your first offer.",
      completed: false,
      actionLabel: "Create campaign",
      actionHref: "/campaigns/new",
    },
  ],
};

export function OnboardingChecklist() {
  const [state, setState] = useState<OnboardingState>(fallbackState);
  const [savingEmbed, setSavingEmbed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    apiFetch("/api/onboarding")
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load onboarding state");
        return response.json() as Promise<OnboardingState>;
      })
      .then((payload) => {
        if (!cancelled) setState(payload);
      })
      .catch(() => {
        if (!cancelled) setState(fallbackState);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const progress = useMemo(() => {
    return Math.round((state.completedCount / state.totalCount) * 100);
  }, [state.completedCount, state.totalCount]);

  async function confirmAppEmbedEnabled() {
    setSavingEmbed(true);
    try {
      const response = await apiFetch("/api/onboarding/app-embed-enabled", {
        method: "POST",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Unable to update onboarding");
      setState((await response.json()) as OnboardingState);
    } finally {
      setSavingEmbed(false);
    }
  }

  return (
    <s-section heading="Onboarding">
      <div className="onboarding-summary">
        <div>
          <strong>{state.completedCount} of {state.totalCount} completed</strong>
          <small>Finish these steps to launch your first offer.</small>
        </div>
        <s-badge tone={progress === 100 ? "success" : "attention"}>{progress}%</s-badge>
      </div>
      <div className="onboarding-progress" aria-label="Onboarding progress">
        <span style={{ width: `${progress}%` }} />
      </div>
      <div className="onboarding-list">
        {state.items.map((item) => (
          <article className="onboarding-item" data-completed={item.completed} key={item.id}>
            <span className="onboarding-status" aria-hidden="true">
              {item.completed ? "✓" : ""}
            </span>
            <div>
              <strong>{item.title}</strong>
              <small>{item.description}</small>
            </div>
            {item.actionHref && item.actionLabel ? (
              <div className="onboarding-actions">
                <a
                  className="button"
                  href={item.actionHref}
                  rel={item.actionHref.startsWith("http") ? "noreferrer" : undefined}
                  target={item.actionHref.startsWith("http") ? "_blank" : undefined}
                >
                  {item.actionLabel}
                </a>
                {item.id === "enable-app-embed" && !item.completed ? (
                  <button type="button" onClick={confirmAppEmbedEnabled} disabled={savingEmbed}>
                    {savingEmbed ? "Saving" : "I enabled it"}
                  </button>
                ) : null}
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </s-section>
  );
}
